import { inject, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    delay,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { PutawayTaskOrchestrator } from '../services/putaway-task-orchestrator.service';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { RoutesForPutaway } from './putaway.routes';
import { PutawayTask } from '../models/putaway-task.model';
import {
    EquipmentDto,
    InventoryBundleFacade,
    PutAwayDto,
    PutAwayFacade as PutAwayLineFacade,
    PutAwayListDataService,
    PutAwayListFacade,
    SlotDto,
    SlotFacade,
    WarehouseFacade,
} from '@pf/platform-wms/domain';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PutawayContext } from '../models/putaway-context.model';
import { LoggerService, UserPermissionCheckService } from '@pf/shared-services';
import { HandlingUnitAssignmentService } from '../services/handling-unit-assignment.service';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { SlotType, ZoneType } from '@control-tower/platform-wms';
import { asArraySafe } from '@pf/shared-utility';

export type PutawayListType = 'all' | 'ltl' | 'parts';

@UntilDestroy()
@Injectable()
export class PutawayFacade {
    private readonly putAwayListFacade = inject(PutAwayListFacade);
    private readonly putAwayLineFacade = inject(PutAwayLineFacade);
    private readonly putAwayListDataService = inject(PutAwayListDataService);
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly taskOrchestrator = inject(PutawayTaskOrchestrator);
    private readonly routes = inject(RoutesForPutaway);
    private readonly logger = inject(LoggerService);
    private readonly handlingUnitService = inject(
        HandlingUnitAssignmentService
    );
    private readonly userPositionFacade = inject(UserPositionFacade);
    private readonly userPermissionCheckService = inject(
        UserPermissionCheckService
    );
    private readonly inventoryBundleFacade = inject(InventoryBundleFacade);
    private readonly slotFacade = inject(SlotFacade);

    private tasksLoadingState = new BehaviorSubject<
        'not-loaded' | 'loading' | 'loaded'
    >('not-loaded');

    typeFilter$ = new BehaviorSubject<PutawayListType>('all');

    private readonly canUseLtl$ = this.userPermissionCheckService
        .checkPermission$('WMS:ForkliftOperator')
        .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    putawayTasks$ = this.taskStateService.tasks$.pipe(
        map(tasks => tasks as PutawayTask[]),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    private filteredTasks$ = combineLatest([
        this.putawayTasks$,
        this.taskStateService.taskFilters$,
        this.typeFilter$,
        this.canUseLtl$,
    ]).pipe(
        map(([tasks, filters, typeFilter, canUseLtl]) =>
            tasks.filter(task => {
                if (!canUseLtl && task.isLtl) return false;
                if (filters.zones.length > 0) {
                    const hasMatchingZone = task.zoneIds.some(id =>
                        filters.zones.includes(id)
                    );
                    if (!hasMatchingZone) return false;
                }
                if (typeFilter === 'ltl' && !task.isLtl) return false;
                if (typeFilter === 'parts' && task.isLtl) return false;
                return true;
            })
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    activeTasks$ = combineLatest([
        this.taskStateService.tasks$,
        this.canUseLtl$,
    ]).pipe(
        map(([tasks, canUseLtl]) =>
            (tasks as PutawayTask[]).filter(
                task => task.section === 'active' && (canUseLtl || !task.isLtl)
            )
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    filteredActiveTasks$ = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'active')),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    filteredPendingTasks$ = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'pending')),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    activePutawayTask$: Observable<PutawayTask> =
        this.taskStateService.activeTask$.pipe(
            map(task => task as PutawayTask),
            shareReplay({ bufferSize: 1, refCount: true })
        );

    putawayContext$: Observable<PutawayContext> = this.activePutawayTask$.pipe(
        filter(task => !!task?.context),
        map(task => task.context),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    getUserSessionPutaways$() {
        return this.userPositionFacade.active$.pipe(
            filter(Boolean),
            map(
                userPosition =>
                    (userPosition!.context as PutawayContext).putAwayListId
            ),
            distinctUntilChanged(),
            switchMap(putAwayListId => {
                if (!putAwayListId) {
                    return of([]);
                }

                return this.putAwayListFacade.getMany$([putAwayListId]);
            })
        );
    }

    loadPutawayLists() {
        this.tasksLoadingState.next('loading');
        this.taskStateService.clearTasks();

        return this.putAwayListFacade.loadPendingPutAwayLists$().pipe(
            untilDestroyed(this),
            switchMap(() => {
                return combineLatest({
                    pendingLists: this.putAwayListFacade.pendingPutAwayLists$(),
                    activeLists: this.getUserSessionPutaways$(),
                });
            }),
            tap(({ pendingLists, activeLists }) => {
                this.taskOrchestrator.orchestrate(
                    this.warehouseFacade.active!.id,
                    pendingLists.filter(list => !list.isDeleted),
                    activeLists.filter(list => !list.isDeleted)
                );
            }),
            tap(() => {
                this.tasksLoadingState.next('loaded');
            }),
            map(() => true),
            catchError(err => {
                this.logger.error(err);
                return of(false);
            })
        );
    }

    loadActivePutawayTask$() {
        if (this.taskStateService.getActiveTask()) {
            return of(true);
        }

        return this.tasksLoadingState.pipe(
            filter(state => state !== 'loading'),
            take(1),
            switchMap(state => {
                if (state === 'not-loaded') {
                    return this.loadPutawayLists().pipe(delay(100));
                }
                return of(true);
            }),
            switchMap(() => this.activeTasks$),
            tap(tasks => {
                if (tasks.length === 0) {
                    this.clearActivePutawayTask();
                } else {
                    this.taskStateService.setActiveTask(tasks[0].id);
                }
            }),
            map(() => true)
        );
    }

    assignEquipmentToPutaway$(equipment: EquipmentDto): Observable<boolean> {
        return this.handlingUnitService.addHandlingUnit$(equipment);
    }

    claimActivePutawayTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PutawayTask) => {
                const putAwayListId = activeTask.putAwayList.id;

                if (!putAwayListId) {
                    return of(false);
                }

                return this.putAwayListDataService
                    .claimPutAwayLists$(activeTask.warehouseId, [putAwayListId])
                    .pipe(
                        tap(() => {
                            this.taskStateService.claimActiveTask();
                        }),
                        tap(() => {
                            this.taskStateService
                                .updateTaskContext$<PutawayContext>(
                                    activeTask.id,
                                    {
                                        putAwayListId,
                                    }
                                )
                                .subscribe();
                        }),
                        map(() => true),
                        catchError(() => of(false))
                    );
            },
            of(false)
        );
    }

    releaseActivePutawayTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PutawayTask) => {
                const putAwayListId = activeTask.putAwayList.id;

                return this.putAwayListDataService
                    .unClaimPutAwayLists$(
                        activeTask.warehouseId,
                        putAwayListId ? [putAwayListId] : []
                    )
                    .pipe(
                        switchMap(() =>
                            this.handlingUnitService.releaseAllHandlingUnits$()
                        ),
                        tap(() => {
                            this.clearActivePutawayTask();
                        }),
                        map(() => true),
                        catchError(() => of(false))
                    );
            },
            of(false)
        );
    }

    setTypeFilter(type: PutawayListType) {
        this.typeFilter$.next(type);
    }

    clearFilters() {
        this.taskStateService.clearTaskFilters();
        this.typeFilter$.next('all');
    }

    navigateToQueue() {
        this.routes.queue().navigate();
    }

    setActivePutawayTask(taskId?: string): void {
        if (!taskId) {
            this.clearActivePutawayTask();
            return;
        }

        this.taskStateService.setActiveTask(taskId);
        this.routes.perform().navigate();
    }

    clearActivePutawayTask() {
        this.taskStateService.clearActiveTask();
        this.navigateToQueue();
    }

    completePutAwayLine$(
        putAwayLineId: string,
        containerId?: string
    ): Observable<boolean> {
        return this.putAwayListFacade
            .completePutAwayLine$(putAwayLineId, containerId)
            .pipe(
                switchMap(() =>
                    this.handlingUnitService.refreshAssignedBundles$()
                ),
                map(() => true)
            );
    }

    suggestRecoverySlot$(itemId: string): Observable<SlotDto | null> {
        return this.inventoryBundleFacade
            .getBundlesByItemAndSlotType$(itemId, SlotType.Recovery)
            .pipe(
                map(result => {
                    const firstBundle = result.data[0];
                    return firstBundle?.container?.slot
                        ? ({ ...firstBundle.container.slot } as SlotDto)
                        : null;
                }),
                switchMap(existingSlot => {
                    if (existingSlot?.id) {
                        return of(existingSlot);
                    }
                    return this.slotFacade.getFirstEmptySlotByTypeAndZoneType$(
                        SlotType.Recovery,
                        ZoneType.Normal
                    );
                }),
                catchError(() => of(null))
            );
    }

    updatePutAwaySlot$(
        putAway: PutAwayDto,
        slotId: string,
        containerId?: string
    ): Observable<PutAwayDto> {
        putAway.putAwayToSlot = { id: slotId };
        putAway.container = containerId ? { id: containerId } : undefined;

        return this.putAwayLineFacade.save$(putAway, { emitEvent: false });
    }

    claimNextPutawayTask$(): Observable<boolean> {
        return this.filteredPendingTasks$.pipe(
            take(1),
            switchMap(pendingTasks => {
                if (!pendingTasks?.length) {
                    this.clearActivePutawayTask();
                    return of(false);
                }
                this.setActivePutawayTask(pendingTasks[0].id);
                return this.claimActivePutawayTask$();
            })
        );
    }

    completeTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PutawayTask) => {
                const userPositionContext = this.userPositionFacade.active
                    ?.context as PutawayContext;
                const taskContext = activeTask.context as PutawayContext;
                const equipmentIds = asArraySafe(
                    userPositionContext?.assignedHandlingUnitIds ??
                        taskContext?.assignedHandlingUnitIds
                );

                return this.handlingUnitService.releaseAllHandlingUnits$().pipe(
                    switchMap(() =>
                        equipmentIds.length
                            ? this.handlingUnitService.releaseEmptyCartEquipment$(
                                  equipmentIds
                              )
                            : of([])
                    ),
                    switchMap(() => this.resetContext(activeTask.id)),
                    tap(() => {
                        this.clearActivePutawayTask();
                    }),
                    map(() => true),
                    catchError(() => {
                        this.clearActivePutawayTask();
                        return of(false);
                    })
                );
            },
            of(false)
        );
    }

    private resetContext(activeTaskId: string) {
        return this.taskStateService.updateTaskContext$<PutawayContext>(
            activeTaskId,
            {
                putAwayListId: '',
                activePutAwayLineId: '',
                assignedHandlingUnitIds: [],
            }
        );
    }
}
